import type { SimpleUserNode } from '@/modules/users'
import { SIMPLE_USERS_QUERY } from '@/modules/users/queries'
import { getUserLabel } from '@/modules/users/util'
import type { DropdownFilter } from '@/types/graphql/enums'
import { getNodes } from '@/utils/graphql'
import { useQuery } from '@apollo/client'
import type { SelectOption } from '@ur-solutions/react-components'
import { useDebounce, useTranslate } from '@ur-solutions/react-hooks'
import React, { useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'
import type { SelectProps as BaseSelectProps } from './Select'
import { Select } from './Select'

type UserSelectNode = Pick<
  SimpleUserNode,
  'id' | 'firstName' | 'lastName' | 'fullName' | 'email'
> &
  Partial<Pick<SimpleUserNode, 'internalNumber' | 'includeInSalaryCalculation'>>

const User = styled.div`
  display: flex;
  align-items: center;

  span.name {
    margin-left: 0.6rem;
  }
`

interface UserSelectProps
  extends Omit<
    BaseSelectProps<string, UserSelectNode>,
    'options' | 'onFetchMore'
  > {
  alwaysInclude?: UserSelectNode | null
  dropdownPageName?: DropdownFilter
  exclude?: string[]
  filterSalaryResponsible?: boolean
  filterLeaderGroup?: boolean
  filterSalaryCalculation?: boolean
}

export const UserSelect: React.FC<UserSelectProps> = ({
  alwaysInclude = null,
  dropdownPageName,
  exclude,
  filterSalaryResponsible = false,
  filterLeaderGroup = false,
  filterSalaryCalculation = false,

  ...selectProps
}) => {
  const { placeholder } = useTranslate({
    placeholder: 'common.select-user',
  })

  const [query, setQuery] = useState('')
  const debouncedQuery = useDebounce(query)

  const { data, loading, fetchMore } = useQuery(SIMPLE_USERS_QUERY, {
    variables: {
      q: debouncedQuery,
      orderBy: 'lastName',
      dropdown: dropdownPageName,
      filterSalaryResponsible: filterSalaryResponsible,
      filterLeaderGroup: filterLeaderGroup,
      includeInSalaryCalculation: filterSalaryCalculation ? true : undefined,
    },
  })

  const getLabel = useCallback((user: UserSelectNode) => {
    const name = getUserLabel(user)

    return (
      <User>
        <span className="name">{name}</span>
      </User>
    )
  }, [])

  const options = useMemo<SelectOption<string, UserSelectNode>[]>(() => {
    if (!data) return []

    const users = getNodes(data?.allUsers)
    const options = users
      .map(user => ({
        value: user.id,
        label: getLabel(user),
        extra: user,
      }))
      .filter(option => !exclude?.includes(option.value))

    if (alwaysInclude === null) return options

    return [
      {
        value: alwaysInclude.id,
        label: getLabel(alwaysInclude),
        extra: alwaysInclude,
      },
      ...options.filter(option => option.extra.id !== alwaysInclude.id),
    ]
  }, [alwaysInclude, data, exclude, getLabel])

  /**
   * Whenever the user select gets close to the bottom, refetch.
   * @remarks Almost a complete copy of `handleScrollBottom` in `UsersTable.tsx`. Might want to refactor to a hook?
   */
  const handleFetchMore = useCallback(async () => {
    if (typeof data === 'undefined') return
    if (loading || data.allUsers.pageInfo?.endCursor === null) return
    try {
      await fetchMore({
        variables: {
          q: debouncedQuery,
          orderBy: 'lastName',
          after: data.allUsers.pageInfo?.endCursor,
        },
      })
    } catch (e) {
      if (e instanceof Error && e.name === 'Invariant Violation') return
      throw e
    }
  }, [data, fetchMore, debouncedQuery, loading])

  return (
    <Select
      options={options}
      loading={loading}
      placeholder={placeholder}
      searchable="custom"
      filterBackground="white"
      optionHoverBackground="white"
      onFilterChange={setQuery}
      onFetchMore={handleFetchMore}
      {...selectProps}
    />
  )
}
